import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from 'remotion';
import type { Scene } from '../types';

interface SceneEffectsProps {
  scenes: Scene[];
}

/**
 * Scene-specific visual effects component
 * Renders OUTSIDE TransitionSeries using global frame for accurate timing
 *
 * Each scene gets unique visual treatment to enhance emotional impact
 */
export const SceneEffects: React.FC<SceneEffectsProps> = ({ scenes }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const currentMs = (frame / fps) * 1000;

  // Find current scene
  const currentScene = scenes.find(
    (scene) => currentMs >= scene.startMs && currentMs < scene.endMs
  );

  if (!currentScene) return null;

  // Calculate progress within current scene (0-1)
  const sceneProgress = (currentMs - currentScene.startMs) / (currentScene.endMs - currentScene.startMs);
  const sceneFrame = Math.round((currentMs - currentScene.startMs) / 1000 * fps);

  switch (currentScene.id) {
    case 1: // Hook - subtle desaturation, cold tone
      return <HookEffect progress={sceneProgress} />;
    case 2: // Contrast - green tint pulse
      return <ContrastEffect frame={sceneFrame} />;
    case 5: // Why? - red glow pulse, screen shake hint
      return <WhyEffect frame={sceneFrame} progress={sceneProgress} />;
    case 7: // CTA - gold shimmer
      return <CTAEffect frame={sceneFrame} fps={fps} progress={sceneProgress} />;
    default:
      return null;
  }
};

/**
 * Scene 1 (Hook): Cold, desaturated overlay to convey the "quiet phone" problem
 */
const HookEffect: React.FC<{ progress: number }> = ({ progress }) => {
  // Fade in the cold effect
  const opacity = interpolate(progress, [0, 0.3], [0, 0.15], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: 'rgba(100, 130, 180, 0.1)',
        opacity,
        mixBlendMode: 'overlay',
        pointerEvents: 'none',
      }}
    />
  );
};

/**
 * Scene 2 (Contrast): Energetic green tint that pulses with "ringing"
 */
const ContrastEffect: React.FC<{ frame: number }> = ({ frame }) => {
  // Pulse the green tint
  const pulseSpeed = 0.2; // Faster pulse for energy
  const pulse = Math.sin(frame * pulseSpeed) * 0.5 + 0.5;
  const opacity = interpolate(pulse, [0, 1], [0.02, 0.08]);

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse at center, rgba(34, 197, 94, ${opacity}) 0%, transparent 70%)`,
        mixBlendMode: 'screen',
        pointerEvents: 'none',
      }}
    />
  );
};

/**
 * Scene 5 (Why?): Dramatic red glow pulse with subtle screen shake
 */
const WhyEffect: React.FC<{ frame: number; progress: number }> = ({ frame, progress }) => {
  // Pulsing red glow - intensifies throughout the scene
  const pulseSpeed = 0.15;
  const pulse = Math.sin(frame * pulseSpeed) * 0.5 + 0.5;
  const baseIntensity = interpolate(progress, [0, 0.3, 1], [0, 0.1, 0.15]);
  const glowOpacity = baseIntensity + pulse * 0.05;

  // Subtle screen shake during "Why?" word (approximately first 1 second)
  const shakeIntensity = progress < 0.2 ? interpolate(progress, [0, 0.1, 0.2], [0, 2, 0]) : 0;
  const shakeX = Math.sin(frame * 0.8) * shakeIntensity;
  const shakeY = Math.cos(frame * 0.6) * shakeIntensity * 0.5;

  return (
    <>
      {/* Red glow pulse */}
      <AbsoluteFill
        style={{
          background: `radial-gradient(ellipse at center, rgba(239, 68, 68, ${glowOpacity}) 0%, transparent 60%)`,
          mixBlendMode: 'screen',
          pointerEvents: 'none',
        }}
      />
      {/* Screen shake overlay (shifts content via a pseudo-transform) */}
      {shakeIntensity > 0 && (
        <AbsoluteFill
          style={{
            boxShadow: `inset ${shakeX}px ${shakeY}px 0 0 transparent`,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* Vignette intensification */}
      <AbsoluteFill
        style={{
          background: `radial-gradient(ellipse at center, transparent 30%, rgba(0, 0, 0, ${0.2 + progress * 0.1}) 100%)`,
          pointerEvents: 'none',
        }}
      />
    </>
  );
};

/**
 * Scene 7 (CTA): Premium gold shimmer effect for "Free Market Report"
 */
const CTAEffect: React.FC<{ frame: number; fps: number; progress: number }> = ({ frame, fps, progress }) => {
  // Gold shimmer sweep across the screen
  const shimmerPosition = interpolate(
    frame % (fps * 2), // Loop every 2 seconds
    [0, fps * 2],
    [-100, 200],
    { extrapolateRight: 'clamp' }
  );

  // Fade in the effect
  const opacity = interpolate(progress, [0, 0.2], [0, 1], { extrapolateRight: 'clamp' });

  // Subtle sparkle particles
  const sparkle1 = Math.sin(frame * 0.1) * 0.5 + 0.5;
  const sparkle2 = Math.sin(frame * 0.15 + 1) * 0.5 + 0.5;
  const sparkle3 = Math.sin(frame * 0.12 + 2) * 0.5 + 0.5;

  return (
    <AbsoluteFill style={{ opacity, pointerEvents: 'none' }}>
      {/* Gold shimmer sweep */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: `linear-gradient(
            90deg,
            transparent ${shimmerPosition - 30}%,
            rgba(252, 211, 77, 0.1) ${shimmerPosition - 15}%,
            rgba(252, 211, 77, 0.2) ${shimmerPosition}%,
            rgba(252, 211, 77, 0.1) ${shimmerPosition + 15}%,
            transparent ${shimmerPosition + 30}%
          )`,
          mixBlendMode: 'screen',
        }}
      />

      {/* Sparkle particles */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: `
            radial-gradient(circle at 20% 30%, rgba(252, 211, 77, ${sparkle1 * 0.15}) 0%, transparent 8%),
            radial-gradient(circle at 75% 25%, rgba(255, 255, 255, ${sparkle2 * 0.12}) 0%, transparent 6%),
            radial-gradient(circle at 60% 70%, rgba(252, 211, 77, ${sparkle3 * 0.1}) 0%, transparent 10%),
            radial-gradient(circle at 35% 80%, rgba(255, 255, 255, ${sparkle1 * 0.08}) 0%, transparent 5%)
          `,
          mixBlendMode: 'screen',
        }}
      />

      {/* Premium gold vignette */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(ellipse at center, rgba(252, 211, 77, 0.05) 0%, transparent 50%)',
          mixBlendMode: 'overlay',
        }}
      />
    </AbsoluteFill>
  );
};

export default SceneEffects;

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
